// Puerto de packages/hce/src/hce/paciente/MedicamentosTab.tsx. Suma
// "Prescribir medicamento" (RecetaWizard) acá también — pedido explícito
// del usuario 2026-09-03: duplicar la misma función que ya vive en
// "005 - Evolución y Prescripciones" (Formulario005.tsx), para poder
// prescribir directo desde esta pestaña sin pasar por Notas. Mismo
// mecanismo de encounter que NotasTab.tsx (`asegurarEncounterDeHoy`, esta
// pestaña no recibe uno por prop) y mismo patrón de refresco manual que
// AlergiasTab.tsx/DocumentosTab.tsx (`useSearchResources` no expone un
// refetch, su 3er valor es un OperationOutcome, no una función).
import { useMedplum } from '@medplum/react-hooks';
import type { DocumentReference, MedicationRequest, Patient, Reference } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { notify } from '@/components/ui/sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { asegurarEncounterDeHoy } from './asegurarEncounterDeHoy';
import { TablaVacia } from './TablaVacia';
import { EnvioDigitalDocumento } from '../formulario/EnvioDigitalDocumento';
import { RecetaWizard } from '../formulario/recetas/RecetaWizard';
import { puedeAccionProfesional, useRolDelPracticante } from '@/lib/useRolDelPracticante';

const VARIANTE_ESTADO: Record<string, 'pine' | 'secondary' | 'destructive'> = { active: 'pine', completed: 'secondary', cancelled: 'destructive', stopped: 'destructive' };

function useMedicamentosConRefresh(patientId: string) {
  const medplum = useMedplum();
  const [version, setVersion] = useState(0);
  const [medicamentos, setMedicamentos] = useState<MedicationRequest[] | undefined>(undefined);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    let cancelado = false;
    setCargando(true);
    // El search param FHIR de MedicationRequest.authoredOn es "authoredon"
    // (sin guion) — ver AlergiasTab.tsx para el mismo tipo de bug con otro recurso.
    medplum
      .searchResources('MedicationRequest', { patient: `Patient/${patientId}`, _sort: '-authoredon', _count: 100 })
      .then((r) => {
        if (!cancelado) {
          setMedicamentos(r as MedicationRequest[]);
          setCargando(false);
        }
      })
      .catch(() => {
        if (!cancelado) setCargando(false);
      });
    return () => {
      cancelado = true;
    };
  }, [medplum, patientId, version]);

  return [medicamentos, cargando, () => setVersion((v) => v + 1)] as const;
}

type EstadoPdfReenvio = { fase: 'cargando' } | { fase: 'listo'; pdf: Uint8Array } | { fase: 'error'; mensaje: string };

/**
 * Reenvío retroactivo de una receta YA emitida (Fase 4, decisión #4 del
 * plan: el botón "Envío digital" también existe desde recetas viejas, no
 * solo al firmar) — lee el `DocumentReference` que `construirReceta`
 * enlazó en `supportingInformation[0]` y descarga el `Binary` (el PDF YA
 * firmado, sin regenerarlo) antes de mostrar `EnvioDigitalDocumento`.
 */
function ReenviarRecetaDialog({
  open,
  onOpenChange,
  medicationRequest,
  subject,
}: {
  readonly open: boolean;
  readonly onOpenChange: (v: boolean) => void;
  readonly medicationRequest: MedicationRequest | null;
  readonly subject: Reference<Patient>;
}): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const [estado, setEstado] = useState<EstadoPdfReenvio>({ fase: 'cargando' });

  useEffect(() => {
    if (!open || !medicationRequest) return;
    let cancelado = false;
    setEstado({ fase: 'cargando' });
    (async () => {
      const docRefRef = medicationRequest.supportingInformation?.[0]?.reference;
      if (!docRefRef) throw new Error(t('receta.sinPdfFirmado'));
      const docRef = await medplum.readReference<DocumentReference>({ reference: docRefRef });
      const url = docRef.content?.[0]?.attachment?.url;
      if (!url) throw new Error(t('receta.sinPdfFirmado'));
      const blob = await medplum.download(url);
      const pdf = new Uint8Array(await blob.arrayBuffer());
      if (!cancelado) setEstado({ fase: 'listo', pdf });
    })().catch((err: unknown) => {
      if (!cancelado) setEstado({ fase: 'error', mensaje: err instanceof Error ? err.message : String(err) });
    });
    return () => {
      cancelado = true;
    };
  }, [open, medicationRequest, medplum, t]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('receta.reenviarTitulo')}</DialogTitle>
        </DialogHeader>
        {estado.fase === 'cargando' && <p className="text-sm text-muted-foreground">{t('fichaPaciente.cargando')}</p>}
        {estado.fase === 'error' && (
          <Alert variant="red">
            <AlertDescription>{estado.mensaje}</AlertDescription>
          </Alert>
        )}
        {estado.fase === 'listo' && (
          <EnvioDigitalDocumento subject={subject} pdf={estado.pdf} fileName="receta.pdf" etiquetaDocumento={t('receta.etiquetaDocumento')} />
        )}
      </DialogContent>
    </Dialog>
  );
}

export function MedicamentosTab(): JSX.Element {
  // BA-C-11: el servidor solo deja prescribir a médico y odontólogo (403 al resto); aquí se oculta el botón que no podría usar.
  const rolEstado = useRolDelPracticante();
  const puedePrescribir = puedeAccionProfesional(rolEstado, 'prescribir');
  // El reenvío de una receta al paciente es «documento al paciente»: el servidor lo rechaza (403) a quien no lo tiene.
  const puedeEnviarAlPaciente = puedeAccionProfesional(rolEstado, 'documento-al-paciente');
  const { t } = useTranslation();
  const medplum = useMedplum();
  const { id } = useParams() as { id: string };
  const [medicamentos, cargando, refrescar] = useMedicamentosConRefresh(id);
  const [wizardAbierto, setWizardAbierto] = useState(false);
  const [encounterRef, setEncounterRef] = useState<string>();
  const [preparando, setPreparando] = useState(false);
  const [reenviarMr, setReenviarMr] = useState<MedicationRequest | null>(null);

  const abrirWizard = async (): Promise<void> => {
    if (!encounterRef) {
      setPreparando(true);
      try {
        setEncounterRef(await asegurarEncounterDeHoy(medplum, id));
      } catch (err) {
        notify({ color: 'red', message: err instanceof Error ? err.message : String(err), autoClose: false });
        setPreparando(false);
        return;
      }
      setPreparando(false);
    }
    setWizardAbierto(true);
  };

  const lista = medicamentos ?? [];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h5 className="text-sm font-semibold">{t('formularios.f005.prescripciones')}</h5>
        {puedePrescribir && (
          <Button type="button" size="sm" variant="outline" disabled={preparando} onClick={() => void abrirWizard()}>
            {t('formularios.f005.prescribirMedicamento')}
          </Button>
        )}
      </div>

      {cargando ? (
        <p className="text-sm text-muted-foreground">{t('fichaPaciente.cargando')}</p>
      ) : lista.length === 0 ? (
        <TablaVacia mensaje={t('formularios.f005.sinRecetas')} />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('fichaPaciente.columnas.fecha')}</TableHead>
              <TableHead>{t('fichaPaciente.columnas.medicamento')}</TableHead>
              <TableHead>{t('fichaPaciente.columnas.pauta')}</TableHead>
              <TableHead>{t('fichaPaciente.columnas.estado')}</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {lista.map((m) => (
              <TableRow key={m.id}>
                <TableCell>{m.authoredOn ? new Date(m.authoredOn).toLocaleDateString('es-EC') : '—'}</TableCell>
                <TableCell>{m.medicationCodeableConcept?.text ?? '—'}</TableCell>
                <TableCell>{m.dosageInstruction?.[0]?.text ?? '—'}</TableCell>
                <TableCell>
                  <Badge variant={VARIANTE_ESTADO[m.status ?? ''] ?? 'secondary'}>{t(`fichaPaciente.estadoReceta.${m.status}`, m.status ?? '—')}</Badge>
                </TableCell>
                <TableCell>
                  {puedeEnviarAlPaciente && m.supportingInformation?.[0]?.reference && (
                    <Button type="button" size="sm" variant="outline" onClick={() => setReenviarMr(m)}>
                      {t('receta.reenviar')}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={wizardAbierto} onOpenChange={setWizardAbierto}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('receta.tituloWizard')}</DialogTitle>
          </DialogHeader>
          {wizardAbierto && (
            <RecetaWizard
              subject={{ reference: `Patient/${id}` }}
              encounter={encounterRef ? { reference: encounterRef } : undefined}
              onCreada={() => {
                setWizardAbierto(false);
                refrescar();
              }}
              onCancelar={() => setWizardAbierto(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      <ReenviarRecetaDialog
        open={reenviarMr !== null}
        onOpenChange={(v) => {
          if (!v) setReenviarMr(null);
        }}
        medicationRequest={reenviarMr}
        subject={{ reference: `Patient/${id}` }}
      />
    </div>
  );
}
